/**
 * 小红书链接与 ID 的纯函数工具（无水印下载 / 注册时间查询页共用）。
 * 移植自搬小书 `xhshub/apps/web/src/lib/xhsLinks.ts`（去掉服务端补救解析）。
 */

/** 从一段分享文本里抠出第一个 URL */
export function extractUrl(text: string): string | null {
  const m = text.match(/https?:\/\/[^\s<>"'）)】\]]+/i);
  return m ? m[0] : null;
}

export function isXhsHost(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host.endsWith('xiaohongshu.com') || host.includes('xhslink.') || host.includes('xhs.link');
  } catch {
    return false;
  }
}

export function isShortLink(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return host.includes('xhslink.') || host.includes('xhs.link');
  } catch {
    return false;
  }
}

/** 用户主页链接 → user_id */
export function userIdFromProfileUrl(url: string): string | null {
  try {
    const m = new URL(url).pathname.match(/\/user\/profile\/([A-Za-z0-9_-]+)/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/** 笔记链接 → noteId + xsec 参数（feed 接口需要） */
export function noteRefFromUrl(url: string): { noteId: string; xsecToken: string; xsecSource: string } | null {
  try {
    const u = new URL(url);
    if (userIdFromProfileUrl(url)) return null;
    const noteId = u.pathname.split('/').filter(Boolean).pop() ?? '';
    if (noteId.length <= 10) return null;
    return {
      noteId,
      xsecToken: u.searchParams.get('xsec_token') ?? '',
      xsecSource: u.searchParams.get('xsec_source') ?? 'pc_feed',
    };
  } catch {
    return null;
  }
}

/** user_id / note_id 前 8 位 hex 是 Unix 秒 */
export function dateFromXhsId(id: string | null | undefined): Date | null {
  const trimmed = (id ?? '').trim();
  if (trimmed.length < 8 || !/^[0-9a-f]{8}/i.test(trimmed)) return null;
  const seconds = parseInt(trimmed.slice(0, 8), 16);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return new Date(seconds * 1000);
}

/** feed 接口请求体（`POST /api/sns/web/v1/feed`） */
export function feedBody(ref: { noteId: string; xsecToken: string; xsecSource: string }) {
  return {
    source_note_id: ref.noteId,
    image_formats: ['jpg', 'webp', 'avif'],
    extra: { need_body_topic: '1' },
    xsec_source: ref.xsecSource,
    xsec_token: ref.xsecToken,
  };
}

// ---- feed 响应解析（/api/sns/web/v1/feed 的 data）----

type Dict = Record<string, unknown>;
const dict = (v: unknown): Dict | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Dict) : null);
const list = (v: unknown): Dict[] => (Array.isArray(v) ? v.map(dict).filter((x): x is Dict => !!x) : []);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

function firstStreamUrl(stream: Dict | null, order: string[]): string | null {
  if (!stream) return null;
  for (const codec of order) {
    const first = list(stream[codec])[0];
    if (!first) continue;
    const backups = first.backup_urls;
    if (Array.isArray(backups) && typeof backups[0] === 'string' && backups[0]) return backups[0];
    const master = str(first.master_url);
    if (master) return master;
  }
  return null;
}

export type FeedNote = {
  noteId: string;
  title: string;
  desc: string;
  type: string;
  videoUrl: string | null;
  coverUrl: string | null;
  /** 实况图的视频分量在 `liveVideoUrl` */
  images: { url: string; liveVideoUrl: string | null }[];
  author: { userId: string; nickname: string; avatar: string } | null;
};

/** `data` = `xhs.request` 返回的 data（上游的 data 字段，含 items） */
export function parseFeed(data: unknown, noteId: string): FeedNote | null {
  const noteCard = dict(list(dict(data)?.items)[0]?.note_card);
  if (!noteCard) return null;

  const images = list(noteCard.image_list).flatMap((img) => {
    const infos = list(img.info_list);
    const url = str(infos.find((i) => i.image_scene === 'WB_DFT')?.url) ?? str(infos[0]?.url) ?? str(img.url_default);
    if (!url) return [];
    const live = img.live_photo === true ? firstStreamUrl(dict(img.stream), ['h264', 'h265']) : null;
    return [{ url, liveVideoUrl: live }];
  });

  const type = str(noteCard.type) ?? 'normal';
  const isVideo = type === 'video';
  const videoUrl = isVideo ? firstStreamUrl(dict(dict(dict(noteCard.video)?.media)?.stream), ['h265', 'h264']) : null;
  const user = dict(noteCard.user);
  const userId = str(user?.user_id) ?? str(user?.userid) ?? str(user?.id);

  return {
    noteId,
    title: str(noteCard.title) ?? '',
    desc: str(noteCard.desc) ?? '',
    type,
    videoUrl,
    // 视频笔记的 image_list 是封面帧，不算可下载图片，但结果卡要有东西可预览
    coverUrl: images[0]?.url ?? null,
    images: isVideo ? [] : images,
    author: userId
      ? { userId, nickname: str(user?.nickname) ?? str(user?.nick_name) ?? '', avatar: str(user?.avatar) ?? '' }
      : null,
  };
}

/** 页面内 `<img>` 用的地址：xhscdn 常给 `http://`，https 页面里会被当混合内容拦掉，显示时升到 https。 */
export function displayImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.replace(/^http:\/\//i, 'https://');
}
